import styled, { css } from 'styled-components';
import ReactDayPicker from 'react-day-picker';
import { always } from 'ramda';
import 'react-day-picker/lib/style.css';

import { smallSpacedHeading, spacedHeading } from '../../../../../theme/typography';
import { themeColor, whenThemePropEqualsTrue } from '../../../../../theme/getters';
import { Icon } from '../../../icon';
import { media } from '../../../../../theme/media';
import { noTapSelection, square } from '../../../../../theme/mixins';

export const DayPicker = styled(ReactDayPicker)`
  width: 100%;

  * {
    outline: none;
  }

  .DayPicker-wrapper {
    padding-bottom: 0;
  }

  .DayPicker-Months {
    ${media.tablet`
      padding: 0 88px;
      justify-content: space-between;
    `}
  }

  .DayPicker-Month {
    width: 100%;
    margin: 0;
    border-collapse: collapse;

    ${media.mobile`
      width: auto;
    `}
  }

  .DayPicker-WeekdaysRow {
    display: flex;
    width: 100%;
    justify-content: space-between;
    margin-bottom: 16px;
    padding: 0 4px;

    ${media.mobile`
      padding: 0;
      margin-bottom: 40px;
      display: table-row;
      margin-bottom: 0;
    `}
  }

  .DayPicker-Weekday {
    ${media.mobile`
      width: 48px;
    `}
  }

  .DayPicker-Week {
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 10px;

    ${media.mobile`
      display: table-row;
      height: 58px;
      margin-bottom: 0px;
    `}

    .DayPicker-Day {
      position: relative;
      box-sizing: border-box;
      flex-basis: ${100 / 7}%;
      margin: 0;
      border-radius: 0;
      padding: 0;
      background-color: transparent !important;
      ${noTapSelection};

      ${media.mobile`
        width: 48px;
      `}

      &:before {
        content: '';
        display: block;
        padding-top: 100%;

        ${media.mobile`
          display: none;
        `}
      }
    }
  }
`;

const whenIsLeft = whenThemePropEqualsTrue('left');

const leftNavIconStyles = whenIsLeft(
  () => css`
    left: 0;
    right: auto;
  `
);

export const NavIcon = styled(Icon).attrs(() => ({ size: 48 }))`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50px);
  cursor: pointer;
  ${noTapSelection};

  ${props =>
    props.inactive &&
    css`
      pointer-events: none;
    `}

  ${leftNavIconStyles};
`;

export const MonthTitleWrapper = styled.div`
  display: table-caption;
  margin-top: ${({ isCurrentMonth }) => (isCurrentMonth ? 0 : 66)}px;
  text-align: center;
  margin-bottom: 32px;

  ${media.tablet`
    margin-top: 0;
  `}
`;

export const MonthTitle = styled.div`
  ${spacedHeading};
`;

export const Weekday = styled.div`
  ${smallSpacedHeading};
  color: ${themeColor('border')} !important;

  font-size: 12px !important;
  line-height: 20px;
  flex-basis: calc(${100 / 7}% - 10px);
  box-sizing: border-box;
  padding: 0 !important;

  ${media.mobile`
    width: 48px;
    font-size: 0.875em !important;
    padding: 0 0 22px !important;
  `}
`;

const whenIsDisabled = whenThemePropEqualsTrue('disabled');
const whenIsSelected = whenThemePropEqualsTrue('selected');
const whenIsStart = whenThemePropEqualsTrue('start');
const whenIsEnd = whenThemePropEqualsTrue('end');
const whenIsToday = whenThemePropEqualsTrue('today');
const whenIsFirstDayOfWeek = whenThemePropEqualsTrue('firstDayOfWeek');
const whenIsLastDayOfWeek = whenThemePropEqualsTrue('lastDayOfWeek');
const whenIsFirstDayOfMonth = whenThemePropEqualsTrue('firstDayOfMonth');
const whenIsLastDayOfMonth = whenThemePropEqualsTrue('lastDayOfMonth');
const whenHasSingleCharDay = whenThemePropEqualsTrue('hasSingleCharDay');

const getDisabled = whenIsDisabled(
  () => css`
    border: transparent;
    color: ${themeColor('disabled')};
  `
);

const getSingleCharDay = whenHasSingleCharDay(
  () => css`
    letter-spacing: normal;
  `
);

const selectedStyles = css`
  color: ${themeColor('white')};
  background-color: ${themeColor('primary')};
`;

const startRadiusStyles = css`
  border-top-left-radius: 50%;
  border-bottom-left-radius: 50%;
`;

const endRadiusStyles = css`
  border-top-right-radius: 50%;
  border-bottom-right-radius: 50%;
`;

const getFirstDayOfWeek = whenIsFirstDayOfWeek(
  () =>
    css`
      ${startRadiusStyles}
    `
);
const getLastDayOfWeek = whenIsLastDayOfWeek(
  () =>
    css`
      ${endRadiusStyles}
    `
);

const getSelected = whenIsSelected(
  () => css`
    ${selectedStyles};
    ${getFirstDayOfWeek};
    ${getLastDayOfWeek};
  `
);

const getStart = whenIsStart(
  () => css`
    ${selectedStyles};
    ${startRadiusStyles};
  `
);

const getEnd = whenIsEnd(
  () => css`
    ${selectedStyles};
    ${endRadiusStyles};
  `
);

const getFirstDayOfMonth = whenIsFirstDayOfMonth(
  () => css`
    ${startRadiusStyles};
  `
);

const getLastDayOfMonth = whenIsLastDayOfMonth(
  () => css`
    ${endRadiusStyles};
  `
);

const getSelectedTodayBackgroundColor = whenIsSelected(
  always(
    css`
      background-color: ${themeColor('white')};
    `
  )
);

const getStartBackgroundColor = whenIsStart(
  always(
    css`
      background-color: ${themeColor('white')};
    `
  )
);

const getToday = whenIsToday(
  () => css`
    border: transparent;
    font-weight: normal;

    &::after {
      content: '';
      position: absolute;
      bottom: 10px;
      left: calc(50% - 2px);
      height: 4px;
      width: 4px;
      background-color: ${themeColor('primary')};
      ${getSelectedTodayBackgroundColor};
      ${getStartBackgroundColor};
      border-radius: 100%;
    }
  `
);

export const Day = styled.div`
  position: absolute;
  ${smallSpacedHeading};
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  ${square('100%')};
  margin: 0;
  top: 0;
  left: 0;
  
  ${media.mobile`
    top: 5px;
    left: 50%;
    transform: translateX(-50%);
    ${square('49px')};
  `}
  
  ${getSingleCharDay};
  ${getDisabled}
  ${getSelected}
  ${getStart}
  ${getEnd}
  ${getToday}
  ${getFirstDayOfMonth}
  ${getLastDayOfMonth}
`;
